import { Button } from '@daily/shared/components/Button';
import { AudioIcon } from '@daily/shared/components/Icons/AudioIcon';
import { CloseIcon } from '@daily/shared/components/Icons/CloseIcon';
import { Stack } from '@daily/shared/components/Stack';
import { Text } from '@daily/shared/components/Text';
import { Tooltip } from '@daily/shared/components/Tooltip';
import { VisuallyHidden } from '@daily/shared/components/VisuallyHidden';
import { useTheme } from '@daily/shared/contexts/Theme';
import {
  useAudioTrack,
  useDevices,
  useLocalSessionId,
  useParticipantProperty,
} from '@daily-co/daily-react';
import classNames from 'classnames';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isTrackOff } from '../../lib/participants';

type RecordState = 'idle' | 'recording' | 'playing' | 'ended';

const MAX_RECORD_TIME = 5000;

export const TestRecording: React.FC = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const audioRef = useRef<HTMLAudioElement>(null);
  const localSessionId = useLocalSessionId();
  const audioTrack = useAudioTrack(localSessionId);
  const [recordState, setRecordState] = useState<RecordState>('idle');
  const [duration, setDuration] = useState(0);
  const [supportsMediaRecorder, setSupportsMediaRecorder] = useState(false);
  const [showTooltipWhenPlayFailed, setShowTooltipWhenPlayFailed] =
    useState(false);

  useEffect(() => {
    setSupportsMediaRecorder(
      MediaRecorder &&
        (MediaRecorder.isTypeSupported('audio/mp4') ||
          MediaRecorder.isTypeSupported('audio/webm'))
    );
  }, []);

  const mediaRecorder = useRef<MediaRecorder>(null);

  const handleClickTest = async () => {
    mediaRecorder.current = new MediaRecorder(
      new MediaStream([audioTrack?.persistentTrack])
    );
    mediaRecorder.current.start();

    const audioChunks = [];
    const start = Date.now();

    mediaRecorder.current.addEventListener('dataavailable', (ev) => {
      audioChunks.push(ev.data);
    });

    const timeout = setTimeout(() => {
      if (mediaRecorder.current.state === 'recording')
        mediaRecorder.current.stop();
    }, MAX_RECORD_TIME);

    mediaRecorder.current.addEventListener('stop', () => {
      clearTimeout(timeout);
      const blob =
        // Safari only returns one blob when recording stopped
        audioChunks.length === 1 && audioChunks[0] instanceof Blob
          ? audioChunks[0]
          : new Blob(audioChunks);
      const audioUrl = URL.createObjectURL(blob);
      const end = Date.now();
      setDuration((end - start) / 1000);
      audioRef.current.src = audioUrl;
      audioRef.current.play().catch(() => {
        /**
         * On iOS the attempt to automatically play() the audio will likely fail.
         * For that case we'll transition to the "ended" state and show a tooltip
         * to indicate to the user, that the play button should be clicked
         * to play the recording.
         */
        setRecordState('ended');
        setShowTooltipWhenPlayFailed(true);
      });
    });

    setRecordState('recording');
  };

  const handleClickStop = () => {
    mediaRecorder.current.stop();
  };

  const handleAudioEnded = () => {
    setRecordState('ended');
  };

  const handlePlay = () => {
    setRecordState('playing');
  };

  const handleClickPlay = () => {
    setShowTooltipWhenPlayFailed(false);
    audioRef.current.currentTime = 0;
    audioRef.current.play();
  };

  const handleClickReset = () => {
    setRecordState('idle');
    audioRef.current.pause();
    audioRef.current.src = null;
  };

  const { hasMicError, microphones } = useDevices();
  const audioTrackState = useParticipantProperty(
    localSessionId,
    'tracks.audio.state'
  );
  const muted = useMemo(
    () => isTrackOff(audioTrackState) || hasMicError,
    [hasMicError, audioTrackState]
  );

  const deviceId = microphones.find((m) => m.selected)?.device?.deviceId;

  useEffect(() => {
    setRecordState('idle');
    if (!audioRef.current) return;
    audioRef.current.pause();
    audioRef.current.src = null;
  }, [deviceId]);

  const tooltipId = useId();

  if (!supportsMediaRecorder) return null;

  return (
    <div className="test-recording">
      {recordState === 'idle' ? (
        <Tooltip
          disabled={!muted}
          title={t('settings.modal.unmuteToTestMic')}
          id={tooltipId}
          placement="top"
        >
          <Button
            id="test-mic"
            disabled={muted}
            fullWidth
            onClick={handleClickTest}
            variant="ghost"
          >
            <Text
              color="muted"
              underline
              style={{ opacity: muted ? 0.6 : undefined }}
            >
              {t('settings.modal.testYourMic')}
            </Text>
          </Button>
        </Tooltip>
      ) : recordState === 'recording' ? (
        <Stack align="center" gap={8} horizontal>
          <span className="recording-dot" />
          <Text>{t('recording.status.recording')}</Text>
          <Button
            id="test-mic-stop"
            className="stop-btn"
            fullWidth
            onClick={handleClickStop}
            variant="ghost"
          >
            {t('recording.stop')}
          </Button>
        </Stack>
      ) : (
        <div className="player">
          <Tooltip
            disabled={!showTooltipWhenPlayFailed}
            id={tooltipId}
            open={showTooltipWhenPlayFailed}
            tabIndex={-1}
            title={t('settings.modal.playRecording')}
          >
            <Button
              id="test-mic-play"
              disabled={recordState === 'playing'}
              onClick={handleClickPlay}
              variant="ghost"
            >
              <AudioIcon size={16} />
              <VisuallyHidden>
                {t('settings.modal.playRecording')}
              </VisuallyHidden>
            </Button>
          </Tooltip>
          <div className="bar">
            <div
              className={classNames('progress', {
                playing: recordState === 'playing',
              })}
            />
          </div>
          <Button
            id="test-mic-reset"
            onClick={handleClickReset}
            variant="ghost"
          >
            <CloseIcon size={16} variant="thick" />
            <VisuallyHidden>{t('general.cancel')}</VisuallyHidden>
          </Button>
        </div>
      )}
      <audio
        ref={audioRef}
        autoPlay
        onEnded={handleAudioEnded}
        onPlay={handlePlay}
      />
      <style jsx>{`
        .test-recording :global(.stop-btn) {
          animation-duration: ${MAX_RECORD_TIME}ms;
          animation-name: fill-left-to-right;
          animation-timing-function: linear;
          animation-fill-mode: forwards;
          animation-iteration-count: 1;
          animation-direction: normal;
          animation-play-state: running;
          background: linear-gradient(
            90deg,
            ${colors.accent} 0%,
            ${colors.accent} 50%,
            ${colors.background} 50%,
            ${colors.background} 100%
          );
          background-size: 200% 100%;
          border-radius: 4px;
          outline: 1px solid ${colors.border};
          padding: 0px 4px;
        }
        .recording-dot {
          background: ${colors.system.red};
          border-radius: 100%;
          display: block;
          height: 7px;
          width: 7px;
        }
        .player {
          background: ${colors.background};
          border-radius: 4px;
          display: flex;
          outline: 1px solid ${colors.border};
        }
        .player :global(button) {
          padding: 0 4px;
        }
        .player :global(button:first-of-type) {
          border-radius: 4px 0 0 4px !important;
        }
        .player :global(button:last-of-type) {
          border-radius: 0 4px 4px 0 !important;
        }
        .bar {
          border-left: 1px solid ${colors.border};
          border-right: 1px solid ${colors.border};
          position: relative;
          width: 64px;
        }
        .progress {
          background: linear-gradient(
            90deg,
            ${colors.backgroundAccent} 0%,
            ${colors.backgroundAccent} 50%,
            ${colors.background} 50%,
            ${colors.background} 100%
          );
          background-size: 200% 100%;
          display: block;
          height: 100%;
          width: 100%;
        }
        .progress.playing {
          animation-duration: ${duration}s;
          animation-name: fill-left-to-right;
          animation-timing-function: linear;
          animation-fill-mode: forwards;
          animation-iteration-count: 1;
          animation-direction: normal;
          animation-play-state: running;
        }
        @keyframes fill-left-to-right {
          from {
            background-position: 100% 0%;
          }
          to {
            background-position: 0% 0%;
          }
        }
      `}</style>
    </div>
  );
};
